import { executeQueuedFollowup } from './queuedFollowup';
import { captureCallTiming } from './callTiming';
import api, { captureCareerSession, idemKey } from './api';
import { executeCallOutcome, pendingCallOutcome, acknowledgeCallOutcome } from './callOutcome';
import { getPreview } from './employeePreview';

// Contact cards stay in memory only. Durable storage contains outcome requests, never cards.
const runs = new Map(), queues = new Map(), listeners = new Set(), workers = new Map();
const TTL = 8 * 60 * 1000;
let activeUser = null;
const scope = user => `${user}:${getPreview()?.preview_token || 'live'}`;
const storageKey = key => `ls_call_queue_v3:${key}`;
const jobPrefix = key => `ls_call_job_v4:${encodeURIComponent(key)}:`;
const jobKey = (key, job) => jobPrefix(key) + encodeURIComponent(job.body.idempotency_key);
const durableStorage = key => key.endsWith(':live') ? localStorage : sessionStorage;
const resourceId = job => job.body.lead_id || job.body.followup_id;
const resourceKey = job => `${job.body.lead_id ? 'lead' : 'followup'}:${resourceId(job)}`;
const needsReview = job => ['review','rejected'].includes(job.status);
const restoreJob = job => ({...job, status: needsReview(job) ? job.status : 'waiting'});
export const activeCallJobs = jobs => jobs.filter(job => !needsReview(job));
const emit = () => listeners.forEach(fn => fn());
const valid = job => job && ['not-reached','no-interest','callback-requested','reached','followup-not-reached','followup-create','followup-update'].includes(job.action)
  && typeof (job.body?.lead_id || job.body?.followup_id) === 'string' && typeof job.body?.idempotency_key === 'string';
function queue(user, key = scope(user)) {
  if (!queues.has(key)) {
    let saved = [];
    try { saved = JSON.parse(sessionStorage.getItem(storageKey(key)) || '[]'); } catch (_) {}
    queues.set(key, Array.isArray(saved) ? saved.filter(valid).map(restoreJob) : []);
  }
  const items = queues.get(key);
  // One durable key per operation: another tab cannot overwrite the entire queue.
  try {
    const storage = durableStorage(key), prefix = jobPrefix(key);
    for (let i=0; i<storage.length; i++) {
      const k = storage.key(i);
      if (!k?.startsWith(prefix)) continue;
      let job;
      try { job = JSON.parse(storage.getItem(k)); } catch (_) { continue; }
      if (valid(job) && !items.some(j => j.body.idempotency_key === job.body.idempotency_key)) items.push(restoreJob(job));
    }
  } catch (_) { /* The next strict write reports inaccessible storage. */ }
  return items;
}
function persist(user, key = scope(user), strict = false) {
  try {
    for (const job of queues.get(key) || []) durableStorage(key).setItem(jobKey(key, job), JSON.stringify(job));
    sessionStorage.removeItem(storageKey(key));
  }
  catch (_) { if (strict) throw new Error('Speicherung auf diesem Gerät nicht möglich. Bitte Ergebnis erneut bestätigen.'); }
  emit();
}
export const callJobs = user => user ? queue(user) : [];
export const subscribeCallJobs = fn => { listeners.add(fn); return () => listeners.delete(fn); };
export const excludedCallLeads = user => queue(user).map(job => job.body.lead_id).filter(Boolean);
export function activateCallWork(user) { activeUser = user ? scope(user) : null; }
export function saveCallRun(user, mode, state) {
  if (user) runs.set(`${scope(user)}:${mode}`, {...state, savedAt:Date.now()});
}
export function readCallRun(user, mode) {
  const key = `${scope(user)}:${mode}`, state = runs.get(key);
  if (!state) return null;
  if (Date.now() - state.savedAt > TTL) {
    // Keep the exclusion ledger for this tab, but never reuse an expired card/lease.
    return {...state, task:null, buffer:[]};
  }
  return state;
}
export function clearCallWork(user) {
  activeUser = null;
  for (const key of runs.keys()) if (key.startsWith(`${user}:`)) {
    if ([`${scope(user)}:cold`, `${scope(user)}:prewarm`].includes(key)) {
      const state = runs.get(key), ids = (state.buffer || []).map(item => item.lead?.id).filter(Boolean).slice(0,6);
      if (ids.length) api.post('/call/batch/release', {lead_ids:ids}, {backgroundSafe:true}).catch(()=>{});
    }
    runs.delete(key);
  }
}
export function enqueueCallOutcome(user, action, body) {
  const key = scope(user), items = queue(user, key);
  const previous = items.find(job => resourceId(job) === (body.lead_id || body.followup_id) && (job.action === action || (job.action !== 'reached' && action !== 'reached')));
  if (previous) {
    if (previous.action !== action) throw new Error('Ein anderes Ergebnis dieses Kontakts wartet noch auf Bestätigung. Bitte Speicherstatus prüfen.');
    return;
  }
  if (activeCallJobs(items).length >= 6) throw new Error('Sechs Ergebnisse warten auf Bestätigung. Bitte zuerst den Speicherstatus prüfen.');
  const sessionReady = captureCareerSession();
  const job = {action, body:{...body, idempotency_key:body.idempotency_key || idemKey()},
    callTiming:captureCallTiming(), session:null, createdAt:Date.now(), attempted:false, status:'waiting'};
  items.push(job);
  try { persist(user, key, true); } catch (error) { items.pop(); throw error; }
  // Captured before advancing the card. A late reply must not borrow the successor's session.
  const ready = Promise.resolve(sessionReady).then(session => { job.session = session; persist(user, key); });
  jobReady.set(job, ready);
  void drainCallJobs(user);
}
const jobReady = new WeakMap();
export async function drainCallJobs(user, retry = false) {
  const key = scope(user);
  if (workers.has(key)) return workers.get(key);
  if (activeUser !== key || scope(user) !== key) return;
  // Import an unfinished request from the previous release without changing its identity.
  const old = getPreview() ? null : pendingCallOutcome(user);
  if (old && !queue(user, key).some(j => j.body.idempotency_key === old.body.idempotency_key)) {
    queue(user, key).unshift({...old, status:'waiting'}); persist(user, key);
  }
  let connectionInterrupted = false;
  const run = (async () => {
    const blockedResources = new Set();
    for (const job of [...queue(user, key)]) {
      if (activeUser !== key || scope(user) !== key) break;
      // Keep each contact's marker/result order without blocking unrelated contacts.
      const resource = resourceKey(job);
      if (blockedResources.has(resource)) {
        job.status = 'review'; job.message = 'Vorheriges Ergebnis dieses Kontakts noch offen';
        persist(user, key); continue;
      }
      if (needsReview(job) && !retry) { blockedResources.add(resource); continue; }
      try {
        await jobReady.get(job);
        if (activeUser !== key || scope(user) !== key) break;
        job.status = 'saving'; persist(user, key);
        const execute = job.action.startsWith('followup-') && job.action !== 'followup-not-reached' ? executeQueuedFollowup : executeCallOutcome;
        await execute(job, updated => {
          if (updated) Object.assign(job, updated);
          persist(user, key);
        }, () => activeUser === key && scope(user) === key);
        durableStorage(key).removeItem(jobKey(key, job));
        const items = queues.get(key), index = items.indexOf(job);
        if (index >= 0) items.splice(index,1);
        acknowledgeCallOutcome(user, resourceId(job));
        window.dispatchEvent(new Event('salesmachine:contact-saved'));
        persist(user, key);
      } catch (error) {
        job.status = error.callOutcomePending ? 'review' : 'rejected';
        job.message = error.message || (error.callOutcomePending ? 'Bestätigung noch offen' : 'Ergebnis nicht bestätigt');
        persist(user, key);
        blockedResources.add(resource);
        // Stop actual network outages, but continue receipt checks for other contacts.
        const cause = error.cause || error;
        if (cause !== error || !error.callOutcomePending) {
          if (!cause.response || cause.response.status >= 500 || cause.response.status === 408) {
            connectionInterrupted = true; break;
          }
        }
      }
    }
  })();
  workers.set(key, run);
  try { await run; } finally {
    workers.delete(key);
    // A click can enqueue while the previous request finishes.
    if (!connectionInterrupted && activeUser === key && queue(user, key).some(j => j.status === 'waiting')) void drainCallJobs(user);
  }
}
export function resetCallWorkForTests() { activeUser=null; runs.clear(); queues.clear(); workers.clear(); listeners.clear(); }


